/**
 * Bring a result card into view and flash it once (related-market chips,
 * Oct 2026). Web only: the card root carries `id={cardDomId(key)}` (nativeID).
 * The card may not exist yet (a page change renders it next frame), so this
 * waits up to ~1s of animation frames for it.
 *
 * The flash is a CSS ring on [data-card-flash="1"] (web/custom.css); under
 * reduced motion the page jumps instead of scrolling and the ring holds still.
 * Focus moves to the card so keyboard and screen-reader users land there too.
 */
import { Platform } from 'react-native';
import { prefersReducedMotion } from './motion';

const FLASH_MS = 1600;
const MAX_FRAMES = 60;

export function revealCard(domId, { doc = typeof document !== 'undefined' ? document : null, raf } = {}) {
    if (Platform.OS !== 'web' || !doc || !domId) return false;
    const nextFrame = raf || ((fn) => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 16)));
    let frames = 0;
    const attempt = () => {
        const el = doc.getElementById(domId);
        if (!el) {
            if (frames++ < MAX_FRAMES) nextFrame(attempt);
            return;
        }
        const reduced = prefersReducedMotion();
        try { el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' }); } catch { /* old browsers */ }
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        try { el.focus({ preventScroll: true }); } catch { /* no focus */ }
        // Restart the flash if the same card is tapped again mid-flash.
        el.removeAttribute('data-card-flash');
        void el.offsetWidth;
        el.setAttribute('data-card-flash', '1');
        clearTimeout(el.__boFlashTimer);
        el.__boFlashTimer = setTimeout(() => el.removeAttribute('data-card-flash'), FLASH_MS);
    };
    attempt();
    return true;
}
